import { apiClient } from './apiClient';
import { 
  Receipt,
  ReceiptListItem,
  CreateReceiptRequest,
  CreateReceiptWithPositionsRequest,
  UpdateReceiptRequest,
  Position,
  CreatePositionRequest,
  UpdatePositionRequest,
  PositionSuggestionResponse,
  PaginatedResponse
} from './types';
import { SyncReceiptRequest } from '../services/sync/types';

export interface GetReceiptsParams {
  onlyDeleted?: boolean;
  search?: string;
  status?: number;
  type?: number;
  dateFrom?: string;
  dateTo?: string;
  pageNumber?: number;
  pageSize?: number;
}

export const receiptService = {
  async getAll(onlyDeleted?: boolean): Promise<ReceiptListItem[]> {
    const params = onlyDeleted !== undefined ? `?onlyDeleted=${onlyDeleted}` : '';
    return apiClient.get<ReceiptListItem[]>(`/receipts${params}`);
  },

  async getAllPaged(params: GetReceiptsParams = {}): Promise<PaginatedResponse<ReceiptListItem>> {
    const queryParams = new URLSearchParams();
    
    if (params.onlyDeleted !== undefined) {
      queryParams.append('onlyDeleted', params.onlyDeleted.toString());
    }
    if (params.search) {
      queryParams.append('search', params.search);
    }
    if (params.status !== undefined) {
      queryParams.append('status', params.status.toString());
    }
    if (params.type !== undefined) {
      queryParams.append('type', params.type.toString());
    }
    if (params.dateFrom) {
      queryParams.append('dateFrom', params.dateFrom);
    }
    if (params.dateTo) {
      queryParams.append('dateTo', params.dateTo);
    }
    if (params.pageNumber !== undefined) {
      queryParams.append('pageNumber', params.pageNumber.toString());
    }
    if (params.pageSize !== undefined) {
      queryParams.append('pageSize', params.pageSize.toString());
    }

    const queryString = queryParams.toString();
    return apiClient.get<PaginatedResponse<ReceiptListItem>>(`/receipts${queryString ? `?${queryString}` : ''}`);
  },

  async getById(id: string): Promise<Receipt> {
    return apiClient.get<Receipt>(`/receipts/${id}`);
  },

  async create(receipt: CreateReceiptRequest): Promise<Receipt> {
    return apiClient.post<CreateReceiptRequest, Receipt>('/receipts', receipt);
  },

  async createWithPositions(request: CreateReceiptWithPositionsRequest): Promise<Receipt> {
    return apiClient.post<CreateReceiptWithPositionsRequest, Receipt>('/receipts/with-positions', request);
  },

  async update(id: string, receipt: UpdateReceiptRequest): Promise<Receipt> {
    return apiClient.put<UpdateReceiptRequest, Receipt>(`/receipts/${id}`, receipt);
  },

  async delete(id: string): Promise<void> {
    await apiClient.delete(`/receipts/${id}`);
  },

  async submit(id: string): Promise<Receipt> {
    return apiClient.post<{}, Receipt>(`/receipts/${id}/submit`, {});
  },

  async repeat(id: string): Promise<Receipt> {
    return apiClient.post<{}, Receipt>(`/receipts/${id}/repeat`, {});
  },

  async addPosition(receiptId: string, position: CreatePositionRequest): Promise<Position> {
    return apiClient.post<CreatePositionRequest, Position>(`/receipts/${receiptId}/positions`, position);
  },

  async updatePosition(receiptId: string, positionId: string, position: UpdatePositionRequest): Promise<Position> {
    return apiClient.put<UpdatePositionRequest, Position>(`/receipts/${receiptId}/positions/${positionId}`, position);
  },

  async deletePosition(receiptId: string, positionId: string): Promise<void> {
    await apiClient.delete(`/receipts/${receiptId}/positions/${positionId}`);
  },

  async restore(id: string): Promise<void> {
    await apiClient.post<{}, void>(`/receipts/${id}/restore`, {});
  },

  /**
   * Синхронізація receipt (єдиний ендпоінт для всіх змін)
   */
  async sync(receiptId: string, receipt: SyncReceiptRequest): Promise<Receipt> {
    return apiClient.put<SyncReceiptRequest, Receipt>(`/receipts/${receiptId}/sync`, receipt);
  },

  /**
   * Отримати версію receipt (легкий запит для polling)
   */
  async getVersion(receiptId: string): Promise<number> {
    return apiClient.get<number>(`/receipts/${receiptId}/version`);
  },

  /**
   * Отримати підказки для позицій на основі історії
   */
  async getPositionSuggestions(query?: string, categoryId?: string, limit: number = 10): Promise<PositionSuggestionResponse[]> {
    const params = new URLSearchParams();
    if (query) {
      params.append('query', query);
    }
    if (categoryId) {
      params.append('categoryId', categoryId);
    }
    params.append('limit', limit.toString());
    return apiClient.get<PositionSuggestionResponse[]>(`/receipts/positions/suggestions?${params.toString()}`);
  }
};
